// src/pages/Vendors/VendorsWithParts.js
import React, {useState, useEffect} from 'react';
import {toast} from 'react-toastify';
import {vendorService, partTypeService} from '../../services/api';
import {Link} from 'react-router-dom';
import {ChevronRightIcon} from '@heroicons/react/24/outline';

const VendorsWithParts = () => {
    const [vendors, setVendors] = useState([]);
    const [partTypes, setPartTypes] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch vendors
            const vendorsResponse = await vendorService.getVendors();

            // Fetch part types
            const partTypesResponse = await partTypeService.getPartTypes();

            // Group part types by vendor
            const partTypesByVendor = {};
            partTypesResponse.data.forEach(partType => {
                if (partType.vendor_id) {
                    if (!partTypesByVendor[partType.vendor_id]) {
                        partTypesByVendor[partType.vendor_id] = [];
                    }
                    // Add the part type to the array - this was missing before
                    partTypesByVendor[partType.vendor_id].push(partType);
                }
            });

            setVendors(vendorsResponse.data);
            setPartTypes(partTypesByVendor);
            setError(null);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load vendors and parts. Please try again later.');
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    const vendorsWithParts = vendors.filter(vendor => partTypes[vendor.id] && partTypes[vendor.id].length > 0);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Vendors with Parts</h1>
                <Link
                    to="/vendors"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    All Vendors
                </Link>
            </div>

            {vendorsWithParts.length === 0 ? (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
                    No vendors with assigned parts found.
                </div>
            ) : (
                 <div className="space-y-4">
                     {vendorsWithParts.map(vendor => (
                         <div key={vendor.id} className="bg-white shadow overflow-hidden sm:rounded-lg">
                             <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                                 <h3 className="text-lg leading-6 font-medium text-gray-900">{vendor.name}</h3>
                                 <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                     {vendor.contact_person ? `Contact: ${vendor.contact_person}` : ''}
                                     {vendor.contact_person && vendor.email ? ' | ' : ''}
                                     {vendor.email ? `Email: ${vendor.email}` : ''}
                                 </p>
                             </div>
                             <div className="border-t border-gray-200">
                                 <dl>
                                     <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                         <dt className="text-sm font-medium text-gray-500">Part Types</dt>
                                         <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                             <div className="space-y-2">
                                                 {partTypes[vendor.id] && partTypes[vendor.id].map(partType => (
                                                     <div key={partType.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-md shadow-sm border border-gray-100">
                                                         <div>
                                                             <p className="font-medium">{partType.name}</p>
                                                             <div className="flex items-center mt-1 text-xs text-gray-500">
                                                                 {partType.vendor_sku && (
                                                                     <span className="mr-3">SKU: {partType.vendor_sku}</span>
                                                                 )}
                                                                 {partType.vendor_price && (
                                                                     <span className="mr-3">Price: ${parseFloat(partType.vendor_price).toFixed(2)}</span>
                                                                 )}
                                                                 <span>Current Stock: {partType.inventory?.current_stock || 0}</span>
                                                             </div>
                                                         </div>
                                                         <Link
                                                             to={`/part-types`}
                                                             className="text-indigo-600 hover:text-indigo-900"
                                                         >
                                                             <ChevronRightIcon className="h-5 w-5"/>
                                                         </Link>
                                                     </div>
                                                 ))}
                                             </div>
                                         </dd>
                                     </div>
                                 </dl>
                             </div>
                         </div>
                     ))}
                 </div>
             )}
        </div>
    );
};

export default VendorsWithParts;
